import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { getJobs, updateJob, createJob } from '../../lib/api'
import { keys } from '../../lib/queryKeys'
import { PageHeader } from '../../components/PageHeader'
import { Entry, EntryList } from '../../components/Entry'
import { m } from '../../paraglide/messages'
import type { StandaloneJob } from '../../types'

export const Route = createFileRoute('/jobs/')({
  component: JobsPage,
})

// Job types with a defined handler on the server (see backend agenda.ts).
const JOB_TYPES = [
  { value: 'weekly_payment_reminder', label: () => m.jobs_type_weekly_payment() },
  { value: 'daily_lesson_summary',    label: () => m.jobs_type_daily_summary() },
] as const

const emptyJob = {
  name:        '',
  description: '',
  cronExpr:    '0 9 * * 1',
  jobType:     JOB_TYPES[0].value as string,
}

function JobsPage() {
  const queryClient = useQueryClient()
  const [editCronId, setEditCronId] = useState<number | null>(null)
  const [cronValue, setCronValue]   = useState('')
  const [showNew, setShowNew]       = useState(false)
  const [newJob, setNewJob]         = useState(emptyJob)
  const [newError, setNewError]     = useState('')

  const { data, isLoading, error } = useQuery<StandaloneJob[]>({
    queryKey: keys.jobs,
    queryFn:  getJobs,
  })

  const mutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) => updateJob(id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.jobs }),
  })

  const createMutation = useMutation({
    mutationFn: () => createJob(newJob),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.jobs })
      setShowNew(false)
      setNewJob(emptyJob)
      setNewError('')
    },
    onError: (err) => setNewError((err as Error).message),
  })

  const handleToggle = (job: StandaloneJob) => {
    mutation.mutate({ id: job.id, body: { active: !job.active } })
  }

  const handleSaveCron = (job: StandaloneJob) => {
    mutation.mutate({ id: job.id, body: { cronExpr: cronValue } })
    setEditCronId(null)
    setCronValue('')
  }

  if (isLoading) return <p className="text-zinc-400">{m.loading()}</p>
  if (error) return <p className="text-red-600 text-sm">{(error as Error).message}</p>

  const jobs = data ?? []

  const fieldCls = 'border border-zinc-300 px-2 py-1.5 text-sm w-full focus:outline-none focus:border-zinc-900'

  return (
    <div>
      <PageHeader title={m.jobs_title()}>
        {!showNew && (
          <button
            onClick={() => setShowNew(true)}
            className="bg-zinc-900 text-white px-3 py-1.5 text-sm hover:bg-zinc-700"
          >
            {m.jobs_new()}
          </button>
        )}
      </PageHeader>

      {showNew && (
        <form
          onSubmit={(e) => { e.preventDefault(); setNewError(''); createMutation.mutate() }}
          className="mb-6 border border-zinc-200 p-4 flex flex-col gap-3 max-w-lg"
        >
          <p className="text-xs text-zinc-400 uppercase tracking-wide">{m.jobs_new_heading()}</p>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-zinc-700">{m.jobs_name()}</span>
            <input
              type="text"
              required
              value={newJob.name}
              onChange={(e) => setNewJob((j) => ({ ...j, name: e.target.value }))}
              className={fieldCls}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-zinc-700">{m.jobs_description()}</span>
            <input
              type="text"
              value={newJob.description}
              onChange={(e) => setNewJob((j) => ({ ...j, description: e.target.value }))}
              className={fieldCls}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-zinc-700">{m.jobs_what()}</span>
            <select
              value={newJob.jobType}
              onChange={(e) => setNewJob((j) => ({ ...j, jobType: e.target.value }))}
              className={fieldCls}
            >
              {JOB_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label()}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-zinc-700">
              {m.jobs_schedule()} <span className="text-zinc-400">{m.jobs_schedule_hint()}</span>
            </span>
            <input
              type="text"
              required
              value={newJob.cronExpr}
              onChange={(e) => setNewJob((j) => ({ ...j, cronExpr: e.target.value }))}
              className={`${fieldCls} font-mono`}
            />
          </label>

          {newError && <p className="text-red-600 text-sm">{newError}</p>}

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="bg-zinc-900 text-white px-3 py-1.5 text-sm hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? m.saving() : m.action_save()}
            </button>
            <button
              type="button"
              onClick={() => { setShowNew(false); setNewJob(emptyJob); setNewError('') }}
              className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
            >
              {m.action_cancel()}
            </button>
          </div>
        </form>
      )}

      <EntryList isEmpty={jobs.length === 0} emptyMessage={m.jobs_empty()}>
        {jobs.map((job) => (
          <Entry
            key={job.id}
            title={job.name}
            meta={job.description ?? '—'}
            submeta={
              editCronId === job.id ? (
                <span className="flex items-center gap-2">
                  <input
                    type="text"
                    value={cronValue}
                    onChange={(e) => setCronValue(e.target.value)}
                    className="border border-zinc-300 px-2 py-1 text-sm w-36 focus:outline-none focus:border-zinc-900 font-mono"
                  />
                  <button
                    onClick={() => handleSaveCron(job)}
                    disabled={mutation.isPending}
                    className="bg-zinc-900 text-white px-3 py-1.5 text-sm hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {m.action_save()}
                  </button>
                  <button
                    onClick={() => { setEditCronId(null); setCronValue('') }}
                    className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
                  >
                    {m.action_cancel()}
                  </button>
                </span>
              ) : (
                <span className="font-mono text-zinc-700">{job.cronExpr}</span>
              )
            }
            status={job.active ? undefined : <span className="text-zinc-400">{m.job_inactive()}</span>}
            actions={
              editCronId !== job.id ? (
                <>
                  <input
                    type="checkbox"
                    aria-label={m.toggle_active({ name: job.name })}
                    checked={job.active}
                    onChange={() => handleToggle(job)}
                    disabled={mutation.isPending}
                    className="cursor-pointer disabled:cursor-not-allowed"
                  />
                  <button
                    onClick={() => { setEditCronId(job.id); setCronValue(job.cronExpr) }}
                    className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
                  >
                    {m.jobs_edit_cron()}
                  </button>
                </>
              ) : undefined
            }
          />
        ))}
      </EntryList>
    </div>
  )
}
